import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight, Shield, Zap, Target, TrendingUp, Brain, ChevronDown, CheckCircle, Award, Sparkles, Globe, Mail, GraduationCap, Briefcase, BarChart2, Users, Lightbulb, Compass } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import LanguageSelector from '../components/LanguageSelector';
import { useState, useRef, useEffect } from 'react';

export const LandingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, isRTL } = useLanguage();
  const videoRef = useRef(null);
  const [videoLoaded, setVideoLoaded] = useState(false);

  // The 4 Core Evaluation Lenses from the brochure
  const coreLenses = [
    {
      icon: Target,
      name: 'IMPACT',
      tagline: 'Leadership Influence & Effectiveness',
      description: 'Assesses how effectively a leader drives results and influences organizational success. This lens evaluates capability to deliver measurable outcomes.',
    },
    {
      icon: Users,
      name: 'FIT',
      tagline: 'Organizational Alignment',
      description: 'Evaluates alignment with organizational culture and role requirements, ensuring the right fit for the team and strategic direction.',
    },
    {
      icon: Zap,
      name: 'AGILITY',
      tagline: 'Adaptability & Responsiveness',
      description: 'Measures adaptability and responsiveness in a rapidly changing environment, highlighting ability to pivot strategies when necessary.',
    },
    {
      icon: Shield,
      name: 'RISK',
      tagline: 'Challenge Management',
      description: 'Identifies potential derailers and challenges that may arise, enabling proactive strategies to mitigate leadership risks.',
    },
  ];

  const stats = [
    { value: '22', label: 'Evaluation Frameworks' },
    { value: '5000+', label: 'Years of Wisdom' },
    { value: '40+', label: 'Page Report' },
    { value: '65', label: 'Scenarios' },
  ];

  return (
    <div className="min-h-screen bg-[#0D0D0D] text-white overflow-hidden" data-testid="landing-page">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-[#0D0D0D]/80 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center gap-3">
              <div className="relative w-12 h-12">
                <div className="absolute inset-0 bg-gradient-to-br from-[#CC5500] to-[#C5A065] rounded-xl" />
                <div className="absolute inset-0.5 bg-[#0D0D0D] rounded-[10px] flex items-center justify-center">
                  <span className="text-[#CC5500] font-bold text-lg">LE</span>
                </div>
              </div>
              <div>
                <h1 className="text-2xl font-heading font-bold tracking-tight">
                  Lead<span className="text-[#CC5500]">Economics</span>™
                </h1>
                <p className="text-[10px] text-gray-500 tracking-widest">LEADERSHIP INTELLIGENCE</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              {/* Language Selector */}
              <LanguageSelector variant="minimal" />
              
              {user ? (
                <Button
                  onClick={() => navigate(user.is_paid ? '/instructions' : '/payment')}
                  className="bg-gradient-to-r from-[#CC5500] to-[#C5A065] hover:opacity-90 text-white border-0 px-6"
                  data-testid="continue-btn"
                >
                  Continue
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              ) : (
                <Button
                  onClick={() => document.getElementById('get-started')?.scrollIntoView({ behavior: 'smooth' })}
                  variant="outline"
                  className="border-white/20 text-white hover:bg-white/10"
                  data-testid="get-started-btn"
                >
                  {t('getStarted')}
                </Button>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section with Video Background */}
      <section className="relative min-h-screen flex items-center pt-20">
        {/* Video Background - User Provided Brain Video */}
        <div className="absolute inset-0 z-0 overflow-hidden">
          <video
            ref={videoRef}
            autoPlay
            muted
            loop
            playsInline
            onLoadedData={() => setVideoLoaded(true)}
            className={`absolute w-full h-full object-cover transition-opacity duration-1000 ${videoLoaded ? 'opacity-60' : 'opacity-0'}`}
          >
            <source src="https://customer-assets.emergentagent.com/job_5b8790b9-1adf-4d36-aa38-b9118774a4e9/artifacts/9snc3b5e_07a420dd-9471-4dea-b240-29766579b9d5.MP4" type="video/mp4" />
          </video>
          <div className="absolute inset-0 bg-gradient-to-b from-[#0D0D0D] via-[#0D0D0D]/70 to-[#0D0D0D]" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0D0D0D] via-transparent to-[#0D0D0D]" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-4xl mx-auto text-center">
            {/* Badge */}
            <div className="inline-flex items-center gap-3 px-5 py-2.5 rounded-full bg-gradient-to-r from-[#CC5500]/20 to-[#C5A065]/20 border border-[#CC5500]/30 mb-8">
              <Sparkles className="h-5 w-5 text-[#CC5500]" />
              <span className="text-sm text-gray-300">Unprecedented Leadership Intelligence</span>
            </div>

            {/* Main Headline */}
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-heading font-bold leading-[1.05] tracking-tight mb-8">
              {t('heroTitle').split(' ').slice(0, 2).join(' ')}
              <span className="block bg-gradient-to-r from-[#CC5500] via-[#C5A065] to-[#CC5500] bg-clip-text text-transparent">
                {t('heroTitle').split(' ').slice(2).join(' ')}
              </span>
            </h1>

            {/* Subheadline - Updated messaging */}
            <p className="text-xl sm:text-2xl text-gray-300 leading-relaxed mb-6 max-w-3xl mx-auto">
              {t('heroSubtitle')}
            </p>
            
            <p className="text-base text-gray-500 mb-10 italic">
              Board-ready, decision-grade intelligence for high-stakes leadership decisions.
            </p>

            {/* Stats */}
            <div className="grid grid-cols-4 gap-6 max-w-2xl mx-auto pt-8 border-t border-white/10">
              {stats.map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-[#CC5500] to-[#C5A065] bg-clip-text text-transparent">
                    {stat.value}
                  </div>
                  <div className="text-[10px] text-gray-500 mt-1 uppercase tracking-wider">{stat.label}</div>
                </div>
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-12">
              <Button
                onClick={() => document.getElementById('get-started')?.scrollIntoView({ behavior: 'smooth' })}
                size="lg"
                className="bg-gradient-to-r from-[#CC5500] to-[#C5A065] hover:opacity-90 text-white px-10 py-7 text-lg"
                data-testid="hero-cta"
              >
                Begin Your Assessment
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce z-10">
          <ChevronDown className="h-6 w-6 text-gray-500" />
        </div>
      </section>

      {/* The 4 Core Evaluation Lenses */}
      <section className="relative py-24 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1 rounded-full bg-[#CC5500]/10 border border-[#CC5500]/20 text-[#CC5500] text-sm mb-4">
              Leadership Evaluation Matrix
            </span>
            <h2 className="text-4xl sm:text-5xl font-heading font-bold mb-4">
              Four Critical <span className="text-[#C5A065]">Lenses</span>
            </h2>
            <p className="text-lg text-gray-400 max-w-3xl mx-auto">
              Every leader is evaluated through four definitive lenses that boards and institutions need for decision-grade intelligence
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {coreLenses.map((lens, index) => (
              <div
                key={index}
                className="group relative bg-gradient-to-br from-white/5 to-transparent border border-white/10 rounded-2xl p-6 hover:border-[#CC5500]/50 transition-all duration-300"
              >
                <div className="flex flex-col h-full">
                  <div 
                    className="w-14 h-14 rounded-xl flex items-center justify-center mb-4 bg-gradient-to-br from-[#CC5500]/20 to-[#C5A065]/10"
                  >
                    <lens.icon className="h-7 w-7 text-[#CC5500]" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-1">{lens.name}</h3>
                  <p className="text-sm text-[#C5A065] mb-3">{lens.tagline}</p>
                  <p className="text-sm text-gray-400 leading-relaxed flex-grow">
                    {lens.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What Makes This Unprecedented */}
      <section className="relative py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="inline-block px-4 py-1 rounded-full bg-[#C5A065]/10 border border-[#C5A065]/20 text-[#C5A065] text-sm mb-4">
                Why LeadEconomics™
              </span>
              <h2 className="text-4xl sm:text-5xl font-heading font-bold mb-6">
                Beyond Surface
                <span className="block text-[#CC5500]">Competence</span>
              </h2>
              <p className="text-lg text-gray-400 mb-8 leading-relaxed">
                LeadEconomics™ goes where traditional assessments cannot. We decode the <strong className="text-white">deeper architecture of leadership impact</strong> — revealing how an individual will think, decide, behave, and perform in real organizational conditions.
              </p>
              
              <div className="space-y-4">
                <div className="flex items-start gap-4 p-5 bg-white/5 rounded-xl border border-white/10">
                  <div className="w-10 h-10 bg-[#CC5500]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Globe className="h-5 w-5 text-[#CC5500]" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold mb-1">22 Proprietary Frameworks</h3>
                    <p className="text-sm text-gray-400">Integrated evaluation across cognitive, behavioral, strategic, and interpersonal dimensions</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4 p-5 bg-white/5 rounded-xl border border-white/10">
                  <div className="w-10 h-10 bg-[#C5A065]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Sparkles className="h-5 w-5 text-[#C5A065]" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold mb-1">Rooted in Indian Wisdom</h3>
                    <p className="text-sm text-gray-400">5,000+ years of validated human understanding, scientifically applied to modern leadership</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4 p-5 bg-gradient-to-r from-[#CC5500]/10 to-[#C5A065]/10 rounded-xl border border-[#CC5500]/30">
                  <div className="w-10 h-10 bg-gradient-to-br from-[#CC5500] to-[#C5A065] rounded-lg flex items-center justify-center flex-shrink-0">
                    <Award className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold mb-1">Board-Ready Intelligence</h3>
                    <p className="text-sm text-gray-400">Defensible, actionable insights for selection, succession, and development decisions</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Visual - Video Card */}
            <div className="relative">
              <div className="relative aspect-[4/3] bg-gradient-to-br from-[#1a1a1a] to-[#0D0D0D] rounded-2xl border border-white/10 overflow-hidden">
                <video
                  autoPlay
                  muted
                  loop
                  playsInline
                  className="absolute inset-0 w-full h-full object-cover opacity-60"
                >
                  <source src="https://customer-assets.emergentagent.com/job_5b8790b9-1adf-4d36-aa38-b9118774a4e9/artifacts/9snc3b5e_07a420dd-9471-4dea-b240-29766579b9d5.MP4" type="video/mp4" />
                </video>
                <div className="absolute inset-0 bg-gradient-to-t from-[#0D0D0D] via-transparent to-[#0D0D0D]/50" />
                
                <div className="absolute bottom-6 left-6 right-6">
                  <div className="bg-black/60 backdrop-blur-sm rounded-xl p-4 border border-white/10">
                    <p className="text-xs text-gray-400 mb-1">What You'll Discover</p>
                    <p className="text-white font-semibold">A complete leadership blueprint that captures the full spectrum of what boards need to know</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Entry Points - Executive & Student */}
      <section className="relative py-24 bg-[#0a0a0a]" id="get-started">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1 rounded-full bg-[#CC5500]/10 border border-[#CC5500]/20 text-[#CC5500] text-sm mb-4">
              Begin Your Journey
            </span>
            <h2 className="text-4xl sm:text-5xl font-heading font-bold mb-4">
              Select Your <span className="text-[#C5A065]">Assessment</span>
            </h2>
            <p className="text-gray-400">Choose the path designed for your stage of leadership</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {/* Executive */}
            <div className="group relative bg-gradient-to-br from-white/5 to-transparent border-2 border-white/10 rounded-2xl p-6 hover:border-[#CC5500]/50 transition-all duration-500 hover:transform hover:scale-[1.02]">
              <div className="absolute inset-0 bg-gradient-to-br from-[#CC5500]/5 to-transparent rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="relative z-10">
                <div className="w-14 h-14 bg-gradient-to-br from-[#CC5500] to-[#C5A065] rounded-xl flex items-center justify-center mb-5 shadow-lg shadow-[#CC5500]/20">
                  <Briefcase className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Executive Assessment</h3>
                <p className="text-sm text-[#C5A065] mb-3">For Professionals (18+)</p>
                <p className="text-gray-400 mb-5 text-sm leading-relaxed">
                  Comprehensive leadership intelligence for executives and business leaders.
                </p>
                <ul className="space-y-2 mb-6 text-sm text-gray-400">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-[#CC5500]" />
                    65 Executive Scenarios
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-[#CC5500]" />
                    40-Page Report
                  </li>
                </ul>
                <Button
                  onClick={() => navigate('/login/professional')}
                  className="w-full bg-gradient-to-r from-[#CC5500] to-[#C5A065] hover:opacity-90 text-white py-5"
                  data-testid="executive-cta"
                >
                  Begin Assessment
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Student - India */}
            <div className="group relative bg-gradient-to-br from-white/5 to-transparent border-2 border-white/10 rounded-2xl p-6 hover:border-[#C5A065]/50 transition-all duration-500 hover:transform hover:scale-[1.02]">
              <div className="absolute inset-0 bg-gradient-to-br from-[#C5A065]/5 to-transparent rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="relative z-10">
                <div className="w-14 h-14 bg-gradient-to-br from-[#C5A065] to-[#CC5500] rounded-xl flex items-center justify-center mb-5 shadow-lg shadow-[#C5A065]/20">
                  <GraduationCap className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Student Assessment</h3>
                <p className="text-sm text-[#C5A065] mb-3">Ages 15-25 (India)</p>
                <p className="text-gray-400 mb-5 text-sm leading-relaxed">
                  Career clarity for students. Discover your ideal career path.
                </p>
                <ul className="space-y-2 mb-6 text-sm text-gray-400">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-[#C5A065]" />
                    55 Career Scenarios
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-[#C5A065]" />
                    Career Path Mapping
                  </li>
                </ul>
                <Button
                  onClick={() => navigate('/login/student')}
                  className="w-full bg-gradient-to-r from-[#C5A065] to-[#CC5500] hover:opacity-90 text-white py-5"
                  data-testid="student-cta"
                >
                  Begin Assessment
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* International Student - FREE */}
            <div className="group relative bg-gradient-to-br from-blue-500/10 to-purple-500/10 border-2 border-blue-500/30 rounded-2xl p-6 hover:border-blue-400/50 transition-all duration-500 hover:transform hover:scale-[1.02]">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-20">
                <span className="inline-flex items-center gap-1 px-4 py-1.5 bg-gradient-to-r from-green-500 to-emerald-500 text-white text-xs font-bold rounded-full shadow-lg">
                  100% FREE
                </span>
              </div>
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-purple-500/5 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="relative z-10 pt-2">
                <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-purple-500 rounded-xl flex items-center justify-center mb-5 shadow-lg shadow-blue-500/20">
                  <Globe className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">International Students</h3>
                <p className="text-sm text-blue-400 mb-3">Outside India</p>
                <p className="text-gray-400 mb-5 text-sm leading-relaxed">
                  Free career assessment for international students worldwide.
                </p>
                <ul className="space-y-2 mb-6 text-sm text-gray-400">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-blue-400" />
                    Full Assessment
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-blue-400" />
                    Instant Report
                  </li>
                </ul>
                <Button
                  onClick={() => navigate('/international')}
                  className="w-full bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white py-5"
                  data-testid="international-cta"
                >
                  Start Free
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Career Clarity for Students */}
      <section id="students" className="relative py-24 bg-gradient-to-br from-[#C5A065]/5 to-[#0D0D0D]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Large Student Section Header */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-3 px-6 py-2 rounded-full bg-[#C5A065]/20 border border-[#C5A065]/30 mb-6">
              <GraduationCap className="h-6 w-6 text-[#C5A065]" />
              <span className="text-[#C5A065] font-semibold text-lg">FOR STUDENTS</span>
            </div>
            <h2 className="text-5xl sm:text-6xl lg:text-7xl font-heading font-bold mb-4">
              <span className="text-white">Career</span>
              <span className="block text-[#C5A065]">Discovery</span>
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Find your perfect career path with AI-powered insights
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h3 className="text-3xl sm:text-4xl font-heading font-bold mb-6">
                Find Your
                <span className="block text-[#C5A065]">Career Clarity</span>
              </h3>
              <p className="text-lg text-gray-400 mb-8 leading-relaxed">
                Confused about which career path to choose? Our student assessment goes beyond generic aptitude tests to reveal your true potential based on <strong className="text-white">cognitive patterns</strong>, <strong className="text-white">interest mapping</strong>, and <strong className="text-white">lifestyle preferences</strong>.
              </p>
              
              <div className="space-y-4">
                <div className="flex items-start gap-4 p-4 bg-white/5 rounded-xl border border-white/10">
                  <div className="w-10 h-10 bg-[#C5A065]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Lightbulb className="h-5 w-5 text-[#C5A065]" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold mb-1">Discover Hidden Strengths</h3>
                    <p className="text-sm text-gray-400">Uncover talents and abilities you didn't know you had</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4 p-4 bg-white/5 rounded-xl border border-white/10">
                  <div className="w-10 h-10 bg-[#C5A065]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Compass className="h-5 w-5 text-[#C5A065]" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold mb-1">Clear Career Direction</h3>
                    <p className="text-sm text-gray-400">Get specific job families and roles that match your profile</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4 p-4 bg-white/5 rounded-xl border border-white/10">
                  <div className="w-10 h-10 bg-[#C5A065]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <TrendingUp className="h-5 w-5 text-[#C5A065]" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold mb-1">Development Roadmap</h3>
                    <p className="text-sm text-gray-400">Actionable steps to build skills for your ideal career</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="bg-gradient-to-br from-[#1a1a1a] to-[#0D0D0D] rounded-2xl border border-white/10 p-8">
                <div className="text-center mb-8">
                  <h3 className="text-xl font-bold text-white mb-2">Your Career Discovery Report</h3>
                  <p className="text-sm text-gray-500">Personalized insights based on your unique profile</p>
                </div>
                
                <div className="space-y-6">
                  <div className="p-4 bg-white/5 rounded-xl border border-white/10">
                    <div className="flex items-center gap-3 mb-2">
                      <Brain className="h-5 w-5 text-[#C5A065]" />
                      <span className="text-white font-medium">Cognitive Style Analysis</span>
                    </div>
                    <p className="text-sm text-gray-400">Understand how you think, learn, and solve problems</p>
                  </div>
                  
                  <div className="p-4 bg-white/5 rounded-xl border border-white/10">
                    <div className="flex items-center gap-3 mb-2">
                      <BarChart2 className="h-5 w-5 text-[#C5A065]" />
                      <span className="text-white font-medium">Interest & Aptitude Mapping</span>
                    </div>
                    <p className="text-sm text-gray-400">Match your interests with career opportunities</p>
                  </div>
                  
                  <div className="p-4 bg-white/5 rounded-xl border border-white/10">
                    <div className="flex items-center gap-3 mb-2">
                      <Target className="h-5 w-5 text-[#C5A065]" />
                      <span className="text-white font-medium">Career Path Recommendations</span>
                    </div>
                    <p className="text-sm text-gray-400">Specific industries and roles suited to your profile</p>
                  </div>
                </div>

                <div className="mt-8 pt-6 border-t border-white/10 text-center">
                  <p className="text-sm text-gray-500 mb-2">Starting at</p>
                  <p className="text-4xl font-bold text-[#C5A065]">₹2,159</p>
                  <p className="text-xs text-gray-500 mt-1">+ 18% GST | International: applicable taxes</p>
                  <Button
                    onClick={() => navigate('/login/student')}
                    className="mt-4 bg-gradient-to-r from-[#C5A065] to-[#CC5500] hover:opacity-90 text-white"
                    data-testid="student-cta-2"
                  >
                    Start Your Career Discovery
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#CC5500]/20 rounded-full filter blur-[150px] animate-pulse" />
        </div>
        
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold mb-6">
            Discover Your
            <span className="block bg-gradient-to-r from-[#CC5500] to-[#C5A065] bg-clip-text text-transparent">
              Leadership Intelligence
            </span>
          </h2>
          <p className="text-lg text-gray-300 mb-8 max-w-2xl mx-auto">
            Join leaders who trust LeadEconomics™ for unprecedented insights that transform careers and organizations.
          </p>
          
          <Button
            onClick={() => document.getElementById('get-started')?.scrollIntoView({ behavior: 'smooth' })}
            size="lg"
            className="bg-white text-[#0D0D0D] hover:bg-gray-100 text-base px-10 py-7 transition-all duration-300 hover:scale-105 hover:shadow-xl group"
            data-testid="final-cta"
          >
            Begin Your Assessment
            <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative border-t border-white/10 py-12 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6 mb-8">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-gradient-to-br from-[#CC5500] to-[#C5A065] rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm">LE</span>
              </div>
              <span className="text-gray-400 text-sm">LeadEconomics™ | leadeconomics.in</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Mail className="h-4 w-4" />
              <a href="mailto:info@leadeconomics.in" className="hover:text-[#C5A065] transition-colors">
                info@leadeconomics.in
              </a>
            </div>
            <p className="text-sm text-gray-500">
              © 2026 LeadEconomics™. Proprietary Leadership Intelligence.
            </p>
          </div>
          
          {/* Company Disclaimer */}
          <div className="border-t border-white/10 pt-6">
            <p className="text-xs text-gray-600 text-center max-w-4xl mx-auto leading-relaxed">
              LeadEconomics™ is a proprietary tool of <span className="text-gray-500">S3 Global Human Capital Solutions</span>. 
              The readings and insights presented are derived from comprehensive personality assessments combined with validated principles from ancient Indian sciences and modern behavioral frameworks.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};
